<template>
    <div>
        <div class="actions has-background-white">
            <section class="section px-4 py-4">
                <nav class="level">
                    <div class="level-left"></div>
                    <div class="level-right">
                        <p class="level-item">
                            <a
                                class="button is-primary is-right"
                                @click.stop="validateAndSave"
                                >{{ $t('Buttons.save') }}</a
                            >
                        </p>
                    </div>
                </nav>
            </section>
        </div>

        <div class="lb-inspiration-form">
            <section class="section py-4 px-4" :ref="'featured_image'">
                <a href="#discover"
                    ><p
                        v-t="$t('Inspirations.discover_project')"
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    ></p
                ></a>

                <div class="field">
                    <label class="label">{{
                        $t('Inspirations.creation.title')
                    }}</label>
                    <div class="control">
                        <input
                            :ref="'title'"
                            class="input"
                            type="text"
                            :v-model="content.title"
                            @input="$emit('updateTitle', $event.target.value)"
                        />
                    </div>
                </div>

                <label class="label">{{
                    $t('Inspirations.creation.background_image')
                }}</label>
                <project-configuration-image
                    :image.sync="content.image"
                    :can-edit="true"
                    class="mb-2"
                    style="max-width: 338px"
                />

                <div class="field">
                    <label class="label">{{
                        $t('Inspirations.creation.duration')
                    }}</label>
                    <div class="control">
                        <input
                            :ref="'duration'"
                            class="input"
                            type="text"
                            :v-model="content.duration"
                            @input="
                                $emit('updateDuration', $event.target.value)
                            "
                        />
                    </div>
                </div>

                <div class="field">
                    <label class="label">{{
                        $t('Inspirations.creation.level')
                    }}</label>
                    <div class="control">
                        <input
                            :ref="'level'"
                            class="input"
                            type="text"
                            :v-model="content.level"
                            @input="$emit('updateLevel', $event.target.value)"
                        />
                    </div>
                </div>
                <div class="field">
                    <label class="label">{{
                        $t('Inspirations.creation.short_description')
                    }}</label>
                    <div class="control">
                        <textarea
                            :ref="'short_description'"
                            class="textarea"
                            rows="4"
                            @input="
                                $emit(
                                    'updateShortDescription',
                                    $event.target.value
                                )
                            "
                        ></textarea>
                    </div>
                </div>
            </section>

            <section class="section py-4 px-4" :ref="'introduction'">
                <a href="#introduction"
                    ><p
                        v-t="$t('Inspirations.introduction')"
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    ></p
                ></a>

                <ckeditor
                    v-model="content.introduction"
                    :config="{ ...baseConfig }"
                    name="introductionEditor"
                    class="py-2"
                ></ckeditor>
            </section>

            <section class="section py-4 px-4" :ref="'project_type'">
                <a href="#project-type"
                    ><p
                        v-t="$t('Inspirations.used_project_type')"
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    ></p
                ></a>
                <div
                    class="message-body-content columns is-multiline mx-0 px-0"
                >
                    <div
                        v-for="projectType in projectTypesFiltered"
                        :key="projectType.name"
                        class="
                            py-5
                            column
                            is-3
                            has-text-centered
                            px-0
                            mb-0
                            has-cursor-pointer
                        "
                    >
                        <div
                            :ref="projectType.name"
                            class="box mx-2 px-2 py-2"
                            :class="
                                selectedTypes.includes(projectType.name)
                                    ? 'selected'
                                    : ''
                            "
                            @click.stop="selectProjectType(projectType.name)"
                        >
                            <figure class="image is-2by1">
                                <img :src="projectType.image" />
                            </figure>
                            <p>
                                {{
                                    $t(
                                        'Inspirations.project_type.' +
                                            projectType.name
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="section py-4 px-4" :ref="'soft_skills'">
                <a href="#zones"
                    ><p
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    >
                        Soft skills
                    </p></a
                >

                <ckeditor
                    v-model="content.softSkills"
                    :config="{ ...baseConfig }"
                    name="softSkillsEditor"
                    class="py-2"
                ></ckeditor>
            </section>

            <section class="section py-4 px-4" :ref="'key_competences'">
                <a href="#zones"
                    ><p
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    >
                        {{ $t('Inspirations.key_competences') }}
                    </p></a
                >
                <div
                    v-for="competency in competencies"
                    :key="competency"
                    class="py-2"
                >
                    <label class="checkbox">
                        <input
                            :id="competency"
                            :value="competency"
                            v-model="activeCompetences"
                            type="checkbox"
                        />
                        {{ $t('Inspirations.competence.' + competency) }}
                    </label>
                </div>
            </section>

            <section class="section py-4 px-4" :ref="'subjects'">
                <a href="#zones"
                    ><p
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    >
                        {{ $t('Inspirations.subjects') }}
                    </p></a
                >
                <div v-for="subject in subjects" :key="subject" class="py-2">
                    <label class="checkbox">
                        <input
                            :id="subject"
                            :value="subject"
                            v-model="activeSubjects"
                            type="checkbox"
                        />
                        {{ $t('Inspirations.subject.' + subject) }}
                    </label>
                </div>
            </section>

            <section class="section py-4 px-4" :ref="'learning_objectives'">
                <a href="#objectives"
                    ><p
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    >
                        {{ $t('Inspirations.objectives') }}
                    </p></a
                >

                <ckeditor
                    v-model="content.learningObjectives"
                    :config="{ ...baseConfig }"
                    name="learningObjectivesEditor"
                    class="py-2"
                ></ckeditor>
            </section>

            <section class="section py-4 px-4" :ref="'activities'">
                <a href="#proposals"
                    ><p
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    >
                        {{ $t('Inspirations.proposals') }}
                    </p></a
                >
                <div v-for="index in activitiesCount" :key="index">
                    <label class="label pt-2">{{
                        $t('Inspirations.activity_number') + index
                    }}</label>
                    <ckeditor
                        v-model="content.activities[index - 1]"
                        :config="{ ...editorConfig }"
                        :name="'activitiesEditor' + index"
                        :id="'activitiesEditor' + index"
                        class="pt-2 pb-4"
                    ></ckeditor>
                </div>

                <button
                    class="button is-primary"
                    @click="activitiesCount = activitiesCount + 1"
                >
                    {{ $t('Inspirations.add_activity') }}
                </button>
            </section>

            <section class="section py-4 px-4 mb-6">
                <a href="#project"
                    ><p
                        class="
                            is-size-4 is-uppercase
                            has-text-weight-bold
                            mb-5
                            is-bordered-bottom
                        "
                    >
                        {{ $t('Inspirations.result') }}
                    </p></a
                >

                <div class="field">
                    <label class="label">{{
                        $t('Inspirations.creation.project_url')
                    }}</label>
                    <div class="control">
                        <input
                            :ref="'project'"
                            class="input"
                            type="text"
                            :v-model="content.project"
                            @input="$emit('updateProject', $event.target.value)"
                        />
                    </div>
                    <p class="has-text-grey is-size-7">
                        {{ $t('Inspirations.project_helper') }}
                    </p>
                </div>
            </section>
        </div>
    </div>
</template>
<script>
import ProjectConfigurationImage from '@/components/ProjectConfigurationImage';
import InputRichText from '@/components/InputRichText';
import _ from 'underscore';
import CKEditor from 'ckeditor4-vue';
import { toast } from 'bulma-toast';
import { createItem } from '@/services/API';

export default {
    components: {
        ProjectConfigurationImage,
        InputRichText,
        ckeditor: CKEditor.component,
    },
    props: {
        content: {
            type: Object,
            default() {
                return {};
            },
        },
    },
    data() {
        return {
            competencies: this.$store.state.Inspirations.KEY_COMPETENCIES,
            subjects: this.$store.state.Inspirations.SUBJECTS,
            selectedTypes: [],
            activeCompetences: [],
            activeSubjects: [],
            editorData: '<p>Content of the editor.</p>',
            editorConfig: {
                uploadUrl:
                    process.env.VUE_APP_ROOT_API + '/api/V1/upload/image',
                filebrowserBrowseUrl: '/browser/browse.php',
                filebrowserUploadUrl:
                    process.env.VUE_APP_ROOT_API + '/api/V1/upload/image',

                extraPlugins: ['filebrowser', 'uploadimage', 'image2'],
                image2_alignClasses: [
                    'image-left',
                    'image-center',
                    'image-right',
                ],
                image2_captionedClass: 'image-captioned',
            },
            baseConfig: {
                toolbar: [
                    [
                        'Bold',
                        'Italic',
                        'Underline',
                        'Subscript',
                        'Superscript',
                        'Cut',
                        'Copy',
                        'Paste',
                        'Pastefromword',
                    ],
                ],
                removeButtons: '',
            },
            activitiesCount: 1,
            activities: [],
        };
    },
    computed: {
        projectTypesFiltered() {
            return _.filter(
                this.$store.state.Project.PROJECT_TYPE,
                (projectType) => {
                    return projectType.available == true;
                }
            );
        },
    },
    watch: {
        activeCompetences() {
            this.$emit('updateCompetences', this.activeCompetences);
        },
        activeSubjects() {
            this.$emit('updateSubjects', this.activeSubjects);
        },
    },
    methods: {
        selectProjectType(projectType) {
            const index = this.selectedTypes.indexOf(projectType);
            if (index > -1) {
                // only splice array when item is found
                this.selectedTypes.splice(index, 1); // 2nd parameter means remove one item only
            } else {
                this.selectedTypes.push(projectType);
            }
            this.$emit('updateProjectTypes', this.selectedTypes);
        },
        async validateAndSave() {
            console.log('saving', this.content);
            let errorMessage = '';
            if (!this.content.title) {
                errorMessage = this.$t('Inspirations.errors.title');
                this.$refs.title.focus();
            } else if (!this.content.image.url) {
                errorMessage = this.$t('Inspirations.errors.featured_image');
                this.$refs['featured_image'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.duration) {
                errorMessage = this.$t('Inspirations.errors.duration');
                this.$refs.duration.focus();
            } else if (!this.content.level) {
                errorMessage = this.$t('Inspirations.errors.level');
                this.$refs.level.focus();
            } else if (!this.content.shortDescription) {
                errorMessage = this.$t('Inspirations.errors.short_description');
                this.$refs['short_description'].focus();
            } else if (!this.content.introduction) {
                errorMessage = this.$t('Inspirations.errors.introduction');
                this.$refs['introduction'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.projectType.length) {
                errorMessage = this.$t('Inspirations.errors.project_type');
                this.$refs['project_type'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.softSkills) {
                errorMessage = this.$t('Inspirations.errors.soft_skills');
                this.$refs['soft_skills'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.keyCompetences.length) {
                errorMessage = this.$t('Inspirations.errors.key_competences');
                this.$refs['key_competences'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.subjects.length) {
                errorMessage = this.$t('Inspirations.errors.subjects');
                this.$refs['subjects'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.learningObjectives) {
                errorMessage = this.$t(
                    'Inspirations.errors.learning_objectives'
                );
                this.$refs['learning_objectives'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (!this.content.activities.length) {
                errorMessage = this.$t('Inspirations.errors.activities');
                this.$refs['activities'].scrollIntoView({
                    behavior: 'smooth',
                });
            } else if (
                !this.content.project ||
                !this.content.project.includes(
                    process.env.VUE_APP_SHARE_ROOT_URL
                )
            ) {
                errorMessage = this.$t('Inspirations.errors.project');
                this.$refs.project.focus();
            }
            if (errorMessage) {
                toast({
                    message: errorMessage,
                    type: 'is-danger',
                    dismissible: true,
                    pauseOnHover: true,
                    animate: { in: 'fadeIn', out: 'fadeOut' },
                    duration: 5000,
                });
            } else {
                console.log('all ok!!');
                let result = await createItem('inspiration', this.content);
                console.log(result);
                if (result.data._id) {
                    this.$router.push({ name: 'inspirations' });
                }
            }
        },
    },
};
</script>


<style scooped>
.box.selected {
    border: 3px solid #0069bb;
}
.box:hover {
    border: 3px solid #0069bb;
}
.lb-inspiration-form {
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    position: absolute;
    top: 55px;
}
.image-left {
    float: left;
    margin-right: 1rem;
}
.image-right {
    float: right;
    margin-left: 1rem;
}
.image-center {
    margin-left: 1rem;
    margin-right: 1rem;
}
.image-center img {
    margin: 0 auto;
}
.is-bordered-bottom {
    border-bottom: 1px solid;
}
.actions {
    position: fixed;
    right: 0;
}
</style>
